import {
  useState, useEffect, useCallback, useContext,
} from 'react';
import usePageMeta from '../../hooks/usePageMeta';
import { AuthContext } from '../../context/AuthContext';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import BusinessList from '../../components/admin/businesses/BusinessList';
import BusinessInspector from '../../components/admin/businesses/BusinessInspector';
import { listWorlds } from '../../services/worlds';
import {
  listBusinesses, saveHours, setClosure, clearBusiness, setClosureBulk,
} from '../../services/businesses';

/**
 * When each place is open.
 *
 * DELIBERATELY THIN, the `AdminWorlds.js` posture rather than the `AdminNpcs.js`
 * one: it loads, holds a draft, and saves. Everything visual lives in
 * `components/admin/businesses/`.
 *
 * NOT IN THE WORLD EDITOR, and that is the main decision here. The zone
 * inspector is where you draw a building, but its Save writes a DRAFT that
 * needs publishing — and publishing rebuilds the map and re-sends WELCOME to
 * everybody standing in it. Hours are not geometry: they are stored in their own
 * collection precisely so they survive a republish, and they apply the moment
 * they are saved. Putting them in that panel would mean one form where two
 * buttons labelled Save meant different things.
 *
 * EVERY SAVE IS LIVE. There is no draft state on the server — the write
 * refreshes the cache and tells the running game, so a shop shuts in front of
 * whoever is standing outside it.
 */
function AdminBusinesses() {
  const { token } = useContext(AuthContext);
  usePageMeta({ title: 'Opening hours', canonicalPath: '/admin/businesses' });

  const [worlds, setWorlds] = useState([]);
  const [mapName, setMapName] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState({ hours: [], closure: 'none', closedUntil: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await listWorlds(token);
        const list = data.worlds || [];
        setWorlds(list);
        // Pick one straight away — a page that opens on "choose a world" when
        // there is only ever one or two is a click that says nothing.
        if (list.length > 0) setMapName((current) => current || list[0].name);
      } catch (error) {
        setStatus({ error: error.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const load = useCallback(async (name) => {
    if (!name) return;
    try {
      const data = await listBusinesses(token, name);
      setBusinesses(data.businesses || []);
    } catch (error) {
      setStatus({ error: error.message });
      setBusinesses([]);
    }
  }, [token]);

  useEffect(() => { load(mapName); }, [load, mapName]);

  const selected = businesses.find((b) => b.zoneId === selectedId) || null;

  const select = (zoneId) => {
    const b = businesses.find((x) => x.zoneId === zoneId);
    setSelectedId(zoneId);
    setStatus(null);
    setDraft({
      hours: (b?.hours || []).map((w) => ({ ...w })),
      closure: b?.closure || 'none',
      closedUntil: b?.closedUntil || '',
    });
  };

  /** Run a write, then reload — the badge must come from the server's answer,
   *  never from what we assumed the write would do. */
  const run = async (work, ok) => {
    setBusy(true);
    setStatus(null);
    try {
      await work();
      await load(mapName);
      setStatus({ ok });
    } catch (error) {
      setStatus({ error: error.message || 'Could not save.' });
    } finally {
      setBusy(false);
    }
  };

  const onSaveHours = () => run(
    () => saveHours(token, mapName, selectedId, { hours: draft.hours }),
    'Hours saved — anybody in that world sees it already.',
  );

  const onSetClosure = () => run(
    () => setClosure(token, mapName, selectedId, {
      closure: draft.closure,
      // The input gives a local wall-clock string; the server wants an instant.
      // Converting HERE rather than there keeps the browser's timezone — the
      // only one that knows what the admin meant — out of the server's business.
      closedUntil: draft.closedUntil ? new Date(draft.closedUntil).toISOString() : null,
    }),
    draft.closure === 'none' ? 'Open again.' : 'Shut — everyone can see it now.',
  );

  /**
   * Every shop on this world, shut with one press.
   *
   * The reason this button exists: midtown carries over a hundred storefronts,
   * and "coming soon" is a statement about the whole district rather than about
   * any one of them.
   *
   * SKIPS ANYTHING ALREADY AUTHORED — the server's default — so a shop somebody
   * gave real opening hours to keeps them, and the reply says how many were
   * left alone rather than pretending it touched everything.
   */
  const onMarkAllSoon = async () => {
    setBusy(true);
    setStatus(null);
    try {
      // Not through `run`, because the message has to carry the server's own
      // counts — "87 shops" and, crucially, how many were left alone. A fixed
      // string here would imply it touched everything.
      const result = await setClosureBulk(token, mapName, { closure: 'soon', kinds: ['shop'] });
      await load(mapName);
      const alone = result.skipped > 0
        ? ` — ${result.skipped} left alone, they already had hours or a closure set.`
        : '.';
      setStatus({ ok: `${result.changed} shop(s) now say "coming soon"${alone}` });
    } catch (error) {
      setStatus({ error: error.message || 'Could not save.' });
    } finally {
      setBusy(false);
    }
  };

  const onClear = () => run(
    async () => {
      await clearBusiness(token, mapName, selectedId);
      setDraft({ hours: [], closure: 'none', closedUntil: '' });
    },
    'Cleared — always open again.',
  );

  if (loading) return <div className="p-8"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Opening hours</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        When each place is open, and whether it is shut. Saved here, live
        immediately — there is nothing to publish. Right now this changes the
        words over a building; there are no interiors to lock yet.
      </p>

      {status && (
        <p className={`mt-3 rounded p-2 text-sm ${
          status.error
            ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
            : 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200'
        }`}
        >
          {status.error || status.ok}
        </p>
      )}

      <label className="mt-4 block text-sm" htmlFor="world">
        <span className="text-gray-700 dark:text-gray-300">World</span>
        <select
          id="world"
          value={mapName}
          onChange={(e) => { setMapName(e.target.value); setSelectedId(null); }}
          className="mt-1 block rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
        >
          {worlds.map((w) => <option key={w.name} value={w.name}>{w.title || w.name}</option>)}
        </select>
      </label>

      {/*
        A whole-district action, deliberately separated from the two-pane
        editor below. "Every shop is coming soon" is a statement about the
        world; everything under it is a statement about one building.
      */}
      <div className="mt-4 flex items-center gap-3 rounded border border-gray-200 p-3 dark:border-gray-800">
        <button
          type="button"
          onClick={onMarkAllSoon}
          disabled={busy}
          className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          Mark every shop &ldquo;coming soon&rdquo;
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Skips any shop that already has hours or a closure set.
        </span>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <BusinessList
          businesses={businesses}
          selectedId={selectedId}
          onSelect={select}
        />

        <BusinessInspector
          business={selected}
          draft={draft}
          onDraftChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          onSaveHours={onSaveHours}
          onSetClosure={onSetClosure}
          onClear={onClear}
          busy={busy}
        />
      </div>
    </div>
  );
}

export default AdminBusinesses;
